import { RolesDigitedEnum } from "@context/auth/types";
import { editionPerm } from "@utils/permissions";
import { CardActionConfig, CardActionsRegistry } from "./types";
import { classroomEditionKind } from "./disableIf";

const CATALOG_ACTIONS: CardActionConfig[] = [
  {
    key: "enroll",
    labelKey: "actions.enroll",
    iconKey: "enroll",
    permission: "learning.enrollment.create.write",
    disableIf: ["is_standalone", "obsolete"],
    group: 1,
  },
  {
    key: "associate-initiatives",
    labelKey: "actions.associate-initiatives",
    iconKey: "associate-initiatives",
    permission: "learning_catalogue.learning_object.associate.initiatives",
    disableIf: ["obsolete"],
    group: 1,
  },
  {
    key: "see-editions",
    labelKey: "actions.see-editions",
    iconKey: "see-editions",
    // editions.physical.retrieve / editions.virtual.retrieve in base al tipo
    // di aula dell'attività.
    permission: (entity) =>
      editionPerm(classroomEditionKind(entity), "retrieve"),
    hideIf: ["notClassroomActivity", "seeEditionsButtonVisible"],
    group: 1,
  },

  {
    key: "enrolled-users",
    labelKey: "actions.enrolled-users",
    iconKey: "enrolled-users",
    permission: "learning.enrollment.read",
    disableIf: ["is_standalone"],
    group: 2,
  },
  {
    key: "pre-enrolled-users",
    labelKey: "actions.pre-enrolled-users",
    iconKey: "pre-enrolled-users",
    permission: "learning.enrollment.read",
    disableIf: ["is_standalone_unless_classroom"],
    group: 2,
  },
  {
    key: "waiting-users",
    labelKey: "actions.waiting-users",
    iconKey: "waiting-users",
    permission: "learning.enrollment.read",
    disableIf: ["is_standalone_unless_classroom"],
    group: 2,
  },
  {
    key: "enrolled-groups",
    labelKey: "actions.enrolled-groups",
    iconKey: "enrolled-groups",
    permission: "learning.enrollment.read",
    disableIf: ["is_standalone"],
    group: 2,
  },

  {
    key: "duplicate",
    labelKey: "actions.duplicate",
    iconKey: "duplicate",
    permission: "learning_catalogue.learning_object.write",
    group: 3,
  },
  {
    key: "edit",
    labelKey: "actions.edit",
    iconKey: "edit",
    permission: "learning_catalogue.learning_object.write",
    readFallback: {
      labelKey: "actions.view",
      iconKey: "view",
      permission: "learning_catalogue.learning_object.read",
    },
    disableIf: ["notOwner"],
    group: 3,
  },

  // {
  //   key: "suspend",
  //   labelKey: "actions.suspend",
  //   iconKey: "suspend",
  //   permission: "learning_catalogue.learning_object.write",
  //   group: 3,
  // },
  {
    key: "delete",
    labelKey: "actions.delete",
    iconKey: "delete",
    permission: "learning_catalogue.learning_object.delete",
    group: 3,
  },
];

const USER_ACTIONS: CardActionConfig[] = [
  {
    key: "enroll",
    labelKey: "actions.enroll",
    iconKey: "enroll",
    permission: "learning.enrollment.create.write",
    group: 1,
  },
  {
    key: "view-lo",
    labelKey: "actions.viewLearningObjects",
    iconKey: "view-lo",
    permission: "usermgmt.users.read&&learning.enrollment.read",
    group: 1,
  },
  {
    key: "download-csv",
    labelKey: "actions.downloadCsv",
    iconKey: "download-csv",
    permission: "usermgmt.users.read",
    group: 1,
  },
  {
    key: "impersonate",
    labelKey: "actions.impersonate",
    iconKey: "impersonate",
    permission: "usermgmt.loginas",
    // Come in v1: il PRODUCT_OWNER può impersonare anche senza usermgmt.loginas.
    bypassRoles: [RolesDigitedEnum.PRODUCT_OWNER],
    disableIf: ["noInitiatives"],
    group: 2,
  },
  {
    key: "manage-authentication",
    labelKey: "actions.manageAuthentication",
    iconKey: "manage-authentication",
    permission: "usermgmt.mfa.admin.totp.reset",
    hideIf: ["totpNotEnabled"],
    group: 2,
  },
  {
    key: "edit",
    labelKey: "actions.edit",
    iconKey: "edit",
    permission: "usermgmt.users.add",
    disableIf: ["terminated"],
    group: 3,
  },
  {
    key: "reactivate",
    labelKey: "actions.reactivate",
    iconKey: "reactivate",
    permission: "usermgmt.users.add",
    hideIf: ["notTerminated"],
    group: 3,
  },
  {
    key: "block",
    labelKey: (u) =>
      String(u?.status).toLowerCase() === "suspended"
        ? "actions.unblock"
        : "actions.block",
    iconKey: (u) =>
      String(u?.status).toLowerCase() === "suspended" ? "unblock" : "block",
    permission: "usermgmt.users.add",
    disableIf: ["terminated"],
    group: 3,
  },
  {
    key: "delete",
    labelKey: "actions.delete",
    iconKey: "delete",
    permission: "usermgmt.users.add",
    hideIf: ["terminated"],
    group: 3,
  },
];

// D01 (decisione: allineare V2 a v1): Iscrivi e Vedi LO usano i permessi delle
// azioni v1 (groups-config.json / groups-dynamic-config.json), cioè
// static_groups.read anche sui gruppi dinamici e senza create.massive.write.
const STATIC_GROUP_ACTIONS: CardActionConfig[] = [
  {
    key: "enroll",
    labelKey: "actions.enroll",
    iconKey: "enroll",
    permission: "usermgmt.corporates.static_groups.read",
    group: 1,
  },
  {
    key: "view-lo",
    labelKey: "actions.viewLearningObjects",
    iconKey: "view-lo",
    permission: "usermgmt.corporates.static_groups.read",
    group: 1,
  },
  {
    key: "enrollment-status",
    labelKey: "actions.enrollmentStatus",
    iconKey: "enrollment-status",
    permission: "usermgmt.corporates.static_groups.read",
    group: 1,
  },
  {
    key: "edit",
    labelKey: "actions.edit",
    iconKey: "edit",
    permission: "usermgmt.corporates.static_groups.write",
    group: 2,
  },
  {
    key: "delete",
    labelKey: "actions.delete",
    iconKey: "delete",
    permission: "usermgmt.corporates.static_groups.write",
    group: 2,
  },
];

const DYNAMIC_GROUP_ACTIONS: CardActionConfig[] = [
  {
    key: "enroll",
    labelKey: "actions.enroll",
    iconKey: "enroll",
    permission: "usermgmt.corporates.static_groups.read",
    group: 1,
  },
  {
    key: "view-lo",
    labelKey: "actions.viewLearningObjects",
    iconKey: "view-lo",
    permission: "usermgmt.corporates.static_groups.read",
    group: 1,
  },
  {
    key: "enrollment-status",
    labelKey: "actions.enrollmentStatus",
    iconKey: "enrollment-status",
    permission: "usermgmt.corporates.dynamic_groups.read",
    group: 1,
  },
  {
    key: "refresh-perimeter",
    labelKey: "actions.refreshPerimeter",
    iconKey: "refresh-perimeter",
    permission: "usermgmt.corporates.dynamic_groups.write",
    group: 1,
  },
  {
    key: "edit",
    labelKey: "actions.edit",
    iconKey: "edit",
    permission: "usermgmt.corporates.dynamic_groups.write",
    group: 2,
  },
  // {
  //   key: "delete",
  //   labelKey: "actions.delete",
  //   iconKey: "delete",
  //   permission: "usermgmt.corporates.dynamic_groups.write",
  //   group: 2,
  // },
];

export const cardActionsRegistry: CardActionsRegistry = {
  activity: CATALOG_ACTIONS,
  course: CATALOG_ACTIONS,
  path: CATALOG_ACTIONS,
  user: USER_ACTIONS,
  "group-static": STATIC_GROUP_ACTIONS,
  "group-dynamic": DYNAMIC_GROUP_ACTIONS,
};
